import type { ReactNode } from 'react';
import type { VariantInput } from '@motorcity/shared';
import { AssetField } from './AssetField';
import { EmiInput, LocalizedInput, PriceInput, StatusSelect } from './Fields';
import { UspEditor } from './UspEditor';
import { SectionHeader } from './ui/primitives';

/**
 * The field set models and variants have in common, so both levels share one
 * form — divergence here would be a content bug, not a feature. Colours are
 * model-only and are edited by `ColorEditor` alongside this.
 */
export function VehicleForm({
  value,
  onChange,
  assetPrefix,
  fieldErrors,
  requireDescription = false,
  statusWarning,
}: {
  value: VariantInput;
  onChange: (next: VariantInput) => void;
  assetPrefix: string;
  fieldErrors?: Record<string, string[]>;
  /** Models need a description and hero image to publish; variants inherit them. */
  requireDescription?: boolean;
  statusWarning?: ReactNode;
}) {
  const set = <K extends keyof VariantInput>(key: K, next: VariantInput[K]) =>
    onChange({ ...value, [key]: next });

  const errorFor = (key: string) => fieldErrors?.[key]?.[0];

  return (
    <>
      <section className="card">
        <SectionHeader
          title="Content"
          description="Names and copy shown on the model page. Switch language with the control in the header."
        />
        <LocalizedInput
          label="Name"
          required
          value={value.name}
          onChange={(name) => set('name', name)}
          error={errorFor('name')}
        />
        <LocalizedInput
          label="Intro"
          hint="One short line under the name."
          value={value.intro}
          onChange={(intro) => set('intro', intro)}
        />
        <LocalizedInput
          label="Description"
          required={requireDescription}
          multiline
          rows={6}
          value={value.description}
          onChange={(description) => set('description', description)}
        />
        <UspEditor value={value.usps ?? []} onChange={(usps) => set('usps', usps)} />
      </section>

      <section className="card">
        <SectionHeader
          title="Imagery"
          description="Drop files straight onto a tile. Responsive WebP sizes are generated on upload."
        />
        <AssetField
          label="Tactical image"
          required={requireDescription}
          hint="The main hero shot."
          prefix={`${assetPrefix}/tactical`}
          value={value.tacticalImage}
          onChange={(tacticalImage) => set('tacticalImage', tacticalImage)}
        />
        <div className="grid-2">
          <AssetField
            label="Icon — front view"
            prefix={`${assetPrefix}/icons`}
            value={value.iconFront}
            onChange={(iconFront) => set('iconFront', iconFront)}
          />
          <AssetField
            label="Icon — side view"
            prefix={`${assetPrefix}/icons`}
            value={value.iconSide}
            onChange={(iconSide) => set('iconSide', iconSide)}
          />
        </div>
      </section>

      <section className="card">
        <SectionHeader
          title="Pricing"
          description="Both figures are optional — switch off anything that should not appear in the app."
        />
        <PriceInput
          label="Starting from — full price"
          value={value.startingPrice}
          onChange={(startingPrice) => set('startingPrice', startingPrice)}
        />
        <EmiInput
          label="Starting from — EMI"
          value={value.startingEmi}
          onChange={(startingEmi) => set('startingEmi', startingEmi)}
        />
      </section>

      <section className="card">
        <SectionHeader title="Publishing" />
        <StatusSelect
          value={value.status}
          onChange={(status) => set('status', status)}
          warning={statusWarning}
        />
      </section>
    </>
  );
}

export const emptyVehicle = (): VariantInput => ({
  name: { en: '', ar: '' },
  usps: [],
  sortOrder: 0,
  status: 'draft',
});
